import { MessageOfTheDayService } from '@core/services/message-of-the-day.service';
import { TranslationService } from '@capturum/complete';
import { AfterContentChecked, ChangeDetectorRef, Component, ViewEncapsulation, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LayoutService } from '@shared/services/layout.service';
import { Observable } from 'rxjs';
import { PageConfig } from '@core/models/page-config.model';
import { delay, filter, map, tap } from 'rxjs/operators';
import { Select } from '@ngxs/store';
import { UserState } from '@core/state/user/user.state';
import { MenuItemService } from '@shared/services/menu-item.service';
import { NavMenuItem } from '@core/models/nav-menu-item.model';
import { AppService } from '@src/app/app.service';
import { FontAwesomeIcon } from '@core/enums/font-awesome-icon.enum';
import { Modules } from '@core/enums/modules.enum';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { UserService } from '@src/app/features/user/services/user.service';
import { Entities } from '@core/components/entities.enum';
import { SupportWidgetService } from '@core/services/support-widget.service';

@Component({
    selector: 'app-basic-layout',
    templateUrl: './basic-layout.component.html',
    styleUrls: ['./basic-layout.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class BasicLayoutComponent implements AfterContentChecked, OnInit {
  @Select(UserState.isImpersonating)
  public isImpersonated$: Observable<boolean>;
  public pageConfig$: Observable<PageConfig>;
  public activeMenuItemKey$: Observable<string>;
  public showMOTD$: Observable<boolean>;
  public timesIcon = FontAwesomeIcon.Times;
  public showHeader = true;
  public isHomePage = false;

  /**
   * Set from `hideSidebar` on the active route. Pages that do not belong to a single module — the Tutorials
   * page spans every module the user has access to — have nothing to put in the module sidebar, so they get
   * the full width instead of a 325px empty column.
   */
  public hideSidebar = false;

  constructor(
    private translateService: TranslateService,
    private layoutService: LayoutService,
    private menuItemService: MenuItemService,
    private appService: AppService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute,
    private messageOfTheDayService: MessageOfTheDayService,
    public userService: UserService,
    private supportWidgetService: SupportWidgetService,
  ) {
    this.pageConfig$ = this.layoutService.pageConfig$.pipe(
      filter<PageConfig>(Boolean),
      delay(0),
    );
    this.layoutService.pageConfig$.subscribe((data) => {
      this.pageConfig$ = new Observable<PageConfig>((o) => o.next(data));
    });

    this.activeMenuItemKey$ = this.menuItemService.getActiveMenuItem().pipe(
      filter(Boolean),
      map((menuItem: NavMenuItem) => {
        return `module-${menuItem.key ?? Modules.Intergrip}`;
      }), delay(1));

    this.showMOTD$ = this.messageOfTheDayService.shouldShowMOTD();
  }

  public ngAfterContentChecked(): void {
    this.cdr.detectChanges();
  }

  public ngOnInit(): void {
    // This layout is the authenticated shell, so it is the one place that runs
    // on every signed-in page load. Booting the support widget here covers the
    // sessions that never pass through the login screen — a page refresh, or
    // returning with a valid token — which the login call sites alone miss.
    // Both widgets guard against booting twice.
    this.supportWidgetService.initialize();

    // Sets isHomePage as well, from the same route read.
    this.applyRouteLayout();

    this.router.events.pipe(filter((routerEvent) => {
      return routerEvent instanceof NavigationEnd;
    })).subscribe(() => {
      this.applyRouteLayout();
    });
  }

  /**
   * Layout decisions that follow the route.
   *
   * showHeader used to be derived from the active menu item, which only changes when a route matches a menu
   * item. A page that matches none of them — the Tutorials page spans every module, so it has no entry —
   * inherited whatever the previous route left behind. Landing there from the home page kept the home
   * layout, and with it no page header and no content padding, so the fixed header sat over the page.
   */
  private applyRouteLayout(): void {
    this.isHomePage = window?.location?.pathname?.indexOf(Entities.My_Products) > -1;
    this.showHeader = !this.isHomePage;
    this.hideSidebar = this.routeHidesSidebar();
  }

  /**
   * The flag is declared on the routed child, so the tree has to be walked down to whatever is actually
   * rendering rather than read off the layout's own route.
   */
  private routeHidesSidebar(): boolean {
    let route = this.route;

    while (route.firstChild) {
      route = route.firstChild;
    }

    return route.snapshot.data?.hideSidebar === true;
  }


  public get isLoading(): boolean {
    return this.appService.isLoading.getValue();
  }

  public get isContentLoading(): boolean {
    return this.appService.isContentLoading.getValue();
  }
}
